import React from 'react';
import { Helmet } from 'react-helmet-async';
import { businessInfo, openingHours, googleReviews } from '../data/businessInfo';
interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  /** Emit LocalBusiness structured data (opening hours + Google rating). Home and Contact only. */
  includeLocalBusiness?: boolean;
}
const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'AutoBodyShop',
  '@id': `${businessInfo.website}/#business`,
  name: businessInfo.name,
  legalName: businessInfo.legalName,
  description: 'Specialist in ramen blinderen, auto wrappen, lichten smoken, ontchromen en AlloyGator velgbescherming in Almere.',
  url: businessInfo.website,
  telephone: businessInfo.phone,
  email: businessInfo.email,
  image: 'https://www.bkcustoms.nl/og-image.jpg',
  priceRange: '€€',
  address: {
    '@type': 'PostalAddress',
    streetAddress: businessInfo.street,
    postalCode: businessInfo.postalCode,
    addressLocality: businessInfo.city,
    addressCountry: businessInfo.country
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: businessInfo.latitude,
    longitude: businessInfo.longitude
  },
  openingHoursSpecification: [{
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: openingHours.schemaDays,
    opens: openingHours.opens,
    closes: openingHours.closes
  }],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: googleReviews.rating,
    reviewCount: googleReviews.count,
    bestRating: 5,
    worstRating: 1
  },
  areaServed: [{
    '@type': 'City',
    name: 'Almere'
  }, {
    '@type': 'AdministrativeArea',
    name: 'Flevoland'
  }],
  sameAs: ['https://www.instagram.com/bk.customs36/', 'https://www.tiktok.com/@bk.customs36'],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Auto styling diensten',
    itemListElement: ['Auto wrappen', 'Ramen tinten', 'Lichten smoken', 'Ontchromen', 'AlloyGator velgbescherming'].map(service => ({
      '@type': 'Offer',
      itemOffered: {
        '@type': 'Service',
        name: service
      }
    }))
  }
};
export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  ogImage = 'https://www.bkcustoms.nl/og-image.jpg',
  ogType = 'website',
  includeLocalBusiness = false
}) => {
  const fullCanonical = canonical ? `${businessInfo.website}${canonical}` : undefined;
  return <Helmet data-id="element-479" data-mp="1fyi3a9wioozu">
      <title data-id="element-480" data-mp="1fy9p8eonrdn2">{title}</title>
      <meta name="description" content={description} data-id="element-481" data-mp="1fxggj8652e2a" />
      {fullCanonical && <link rel="canonical" href={fullCanonical} data-id="element-482" data-mp="127d233di3one" />}
      {/* Open Graph tags */}
      <meta property="og:title" content={title} data-id="element-483" data-mp="1767t2slmgjil" />
      <meta property="og:description" content={description} data-id="element-484" data-mp="174woiqkcr7qf" />
      <meta property="og:type" content={ogType} data-id="element-485" data-mp="1uw2372ek3tz5" />
      {fullCanonical && <meta property="og:url" content={fullCanonical} data-id="element-486" data-mp="1utpe85adsjcl" />}
      <meta property="og:image" content={ogImage} data-id="element-487" data-mp="1znk45qgkxuk4" />
      <meta property="og:site_name" content="BKCustoms" data-id="element-488" data-mp="1zobryf3xqnzu" />
      <meta property="og:locale" content="nl_NL" data-id="element-489" data-mp="28zzo4o3jw67l" />
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" data-id="element-490" data-mp="250if5kox0qxo" />
      <meta name="twitter:title" content={title} data-id="element-491" data-mp="2ir42bllejn1d" />
      <meta name="twitter:description" content={description} data-id="element-492" data-mp="2ipsqcyhsz8z6" />
      <meta name="twitter:image" content={ogImage} data-id="element-493" data-mp="2dris2f0s7s9c" />
      {/* Additional SEO tags */}
      <meta name="robots" content="index, follow" data-id="element-494" data-mp="1q2ga1628jxeb" />
      <meta name="language" content="Dutch" data-id="element-495" data-mp="1q2yhyzy9g022" />
      <meta name="author" content="BKCustoms" data-id="element-496" data-mp="1m0uvlga1zb20" />
      {/* Structured data: business details, opening hours and Google rating */}
      {includeLocalBusiness && <script type="application/ld+json" data-id="element-497" data-mp="201xgvqt84hbv">
          {JSON.stringify(localBusinessSchema)}
        </script>}
    </Helmet>;
};